import { useState, useRef, useEffect } from 'react';
import { useSample } from '../SampleContext';
import { useLayout } from '../LayoutContext';
import { useLocation } from 'react-router-dom';

interface UploaderProps {
    setScreen: (screen: 'intro' | 'recorder' | 'uploader' | 'webcam' | 'form') => void;
}


interface FileWithDuration {
    file: File;
    duration: number | null;
}

// Utility function to validate CSS hex color
const isValidHexColor = (color: string): boolean => {
    return /^#([0-9A-F]{3}){1,2}$/i.test(color);
};

const Uploader: React.FC<UploaderProps> = ({ setScreen }) => {
    const { setUploadedFiles, setSampleDuration } = useSample();
    const { layout } = useLayout();
    const location = useLocation();
    const [files, setFiles] = useState<FileWithDuration[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const audioContextRef = useRef<AudioContext | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Determine file type based on route
    const isVideoRoute = location.pathname.includes('/video');
    const acceptType = isVideoRoute ? 'video/*' : 'audio/*';

    // Fallback to default colors if invalid
    const safeColors = {
        buttons: isValidHexColor(layout.color.buttons) ? layout.color.buttons : '#22FFAF',
        text: isValidHexColor(layout.color.text) ? layout.color.text : '#000000',
        hover: isValidHexColor(layout.color.hover) ? layout.color.hover : '#1DE9A3',
    };

    // Format duration to MM:SS
    const formatDuration = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Calculate total duration
    const getTotalDuration = (): number => {
        return Number(files.reduce((sum, f) => sum + (f.duration || 0), 0).toFixed(1));
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setIsLoading(true);
            const newFiles = Array.from(event.target.files);
            const newFileDurations: FileWithDuration[] = [];

            if (!audioContextRef.current) {
                audioContextRef.current = new AudioContext();
            }

            try {
                for (const file of newFiles) {
                    try {
                        const arrayBuffer = await file.arrayBuffer();
                        let audioBuffer: AudioBuffer;

                        if (isVideoRoute) {
                            // For video files, create a temporary video element to extract audio
                            const video = document.createElement('video');
                            video.src = URL.createObjectURL(file);
                            await new Promise((resolve) => {
                                video.onloadedmetadata = resolve;
                            });

                            // Use Web Audio API to process video audio
                            const source = audioContextRef.current!.createMediaElementSource(video);
                            const offlineContext = new OfflineAudioContext(
                                2, // Stereo
                                Math.ceil(video.duration * audioContextRef.current!.sampleRate),
                                audioContextRef.current!.sampleRate
                            );
                            const offlineSource = offlineContext.createBufferSource();
                            const arrayBufferCopy = await file.arrayBuffer();
                            audioBuffer = await offlineContext.decodeAudioData(arrayBufferCopy);
                            offlineSource.buffer = audioBuffer;
                            offlineSource.connect(offlineContext.destination);
                            offlineSource.start();
                            audioBuffer = await offlineContext.startRendering();
                            URL.revokeObjectURL(video.src);
                        } else {
                            // For audio files, decode directly
                            audioBuffer = await audioContextRef.current!.decodeAudioData(arrayBuffer);
                        }

                        const fileDuration = audioBuffer.duration;
                        newFileDurations.push({
                            file,
                            duration: Number(fileDuration.toFixed(1)),
                        });
                    } catch (error) {
                        console.error(`Failed to read duration for ${file.name}:`, error);
                        alert(`Failed to read duration for ${file.name}. Skipping this file.`);
                    }
                }

                setFiles((prevFiles) => [...prevFiles, ...newFileDurations]);
            } catch (error) {
                console.error('Error processing files:', error);
                alert('An error occurred while processing the files.');
            } finally {
                setIsLoading(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        }
    };

    const handleRemoveFile = (index: number) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const handleProceed = () => {
        if (files.length === 0) {
            alert(`Please select at least one ${isVideoRoute ? 'video' : 'audio'} file to upload.`);
            return;
        }

        const totalDuration = getTotalDuration();
        if (totalDuration < 60) {
            alert(`Total ${isVideoRoute ? 'video' : 'audio'} duration must be at least 60 seconds.`);
            return;
        }

        setUploadedFiles(files.map((f) => f.file));
        setSampleDuration(totalDuration);
        setScreen('form');
    };

    const totalDuration = getTotalDuration();

    useEffect(() => {
        setSampleDuration(totalDuration);
    }, [totalDuration, setSampleDuration]);

    useEffect(() => {
        return () => {
            if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }
        };
    }, []);

    return (
        <div
            className={`w-full max-w-[375px] p-6 rounded-xl bg-black shadow-[0_4px_20px_rgba(0,0,0,0.5)] flex flex-col items-center `}
        >
            <h2 className={`text-[24px] font-semibold text-[${safeColors.buttons}] mb-6`}>Upload {isVideoRoute ? 'Video' : 'Audio'} Sample</h2>
            <div className="text-white mb-6 text-center text-sm">
                Upload your {isVideoRoute ? 'video' : 'audio'} files here, make sure to provide at least a minute of {isVideoRoute ? 'video' : 'audio'}, ideally more
            </div>
            <input
                type="file"
                accept={acceptType}
                multiple
                className={`rounded-lg p-3 mb-4 w-full text-sm cursor-pointer mt-[44px] bg-[${safeColors.buttons}] hover:bg-[${safeColors.hover}] text-[${safeColors.text}]`}
                onChange={handleFileChange}
                ref={fileInputRef}
            />
            {isLoading && <div className="text-white mb-4 text-sm">Loading durations...</div>}
            {files.length > 0 && (
                <div className="w-full mb-4 max-h-40 overflow-y-auto">
                    {files.map((f, index) => (
                        <div key={index} className="flex justify-between items-center text-white text-sm mb-2">
                            <span>
                                {f.file.name} ({f.duration !== null ? formatDuration(f.duration) : 'N/A'})
                            </span>
                            <button className="text-red-500 hover:text-red-700" onClick={() => handleRemoveFile(index)}>
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {files.length > 0 && (
                <div
                    className={`mb-6 text-lg ${totalDuration < 60 ? 'text-red-500' : totalDuration <= 300 ? 'text-yellow-500' : 'text-green-500'}`}
                >
                    Total Duration: {formatDuration(totalDuration)}
                </div>
            )}
            <div className="flex gap-[60px] mt-[44px]">
                <button
                    className={`border-none rounded-lg px-4 py-[11px] font-bold flex items-center gap-3 text-base cursor-pointer bg-[${safeColors.buttons}] hover:bg-[${safeColors.hover}] text-[${safeColors.text}]`}
                    onClick={() => {
                        setScreen('intro');
                        setUploadedFiles(null);
                        setSampleDuration(0);
                    }}
                >
                    Back
                </button>
                <button
                    className={`border-none rounded-lg px-4 py-[11px] font-bold flex items-center gap-3 text-base bg-[${safeColors.buttons}] hover:bg-[${safeColors.hover}] text-[${safeColors.text}] ${files.length > 0 && totalDuration >= 60 ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
                        }`}
                    onClick={handleProceed}
                    disabled={files.length === 0 || totalDuration < 60}
                >
                    Proceed
                </button>
            </div>
        </div>
    );
};

export default Uploader;